/* eslint-disable @typescript-eslint/no-unused-vars */
import { DateTime } from 'luxon';
import type {
    FutureDelivery,
    ProductBackorder,
} from '../queries/useProductBackorders';

export enum BackorderMessages {
    AppointmentDelivery = '{count} to be delivered on {date}',
    Backorder = '{count} on backorder',
    Unknown = '{count} delivery date to be determined',
    Immediately = '{count} available immediately',
    Soon = '{count} available short-term',
    InDay = '{count} available in approx. {days} days',
    FutureDate = '{count} available in CW {week}/{year}',
}

export interface BackorderStatus {
    message: BackorderMessages;
    values: Record<string, string | number> & { count: number };
}

type BackorderDelivery = Pick<
    ProductBackorder,
    | 'pendingQuantity'
    | 'requestedShippingDate'
    | 'stockAvailableNow'
    | 'stockAvailableSoon'
    | 'stockAvailableLater'
    | 'stockAvailableDelay'
    | 'backorderMarginDays'
    | 'futureDeliveries'
>;

// eslint-disable-next-line max-statements
const backorderBreakdownToMessages = ({
    stockAvailableNow,
    stockAvailableSoon,
    stockAvailableLater,
    backorderMarginDays,
    appointmentDeliveries,
    futureDeliveries,
    stockAvailableDelay,
    unknownCount,
}: {
    stockAvailableNow: number;
    stockAvailableSoon: number;
    stockAvailableLater: number;
    backorderMarginDays?: number;
    appointmentDeliveries: [string, number][];
    stockAvailableDelay: number;
    futureDeliveries: (FutureDelivery & { amount: number })[];
    unknownCount: number;
}) => {
    const result: BackorderStatus[] = [];

    if (stockAvailableNow) {
        result.push({
            message: BackorderMessages.Immediately,
            values: {
                count: stockAvailableNow,
            },
        });
    }

    if (stockAvailableSoon) {
        result.push({
            message: BackorderMessages.Soon,
            values: {
                count: stockAvailableSoon,
            },
        });
    }
    if (stockAvailableLater) {
        result.push({
            message: BackorderMessages.InDay,
            values: {
                count: stockAvailableLater,
                days: backorderMarginDays || 5,
            },
        });
    }

    for (const futureDelivery of futureDeliveries) {
        result.push({
            message: BackorderMessages.FutureDate,
            values: {
                count: futureDelivery.amount,
                week: futureDelivery.calendarWeek,
                year: futureDelivery.year,
            },
        });
    }

    for (const [requestedShippingDate, quantity] of appointmentDeliveries) {
        result.push({
            message: BackorderMessages.AppointmentDelivery,
            values: {
                date: DateTime.fromSQL(requestedShippingDate).toFormat(
                    'dd.MM.yyyy'
                ),
                count: quantity,
            },
        });
    }

    const totalUnknownCount = unknownCount + stockAvailableDelay;
    if (totalUnknownCount > 0) {
        result.push({
            message: BackorderMessages.Unknown,
            values: {
                count: totalUnknownCount,
            },
        });
    }

    return result;
};

const isAppointmentDelivery = (requestedShippingDate: string) => {
    const requestedShippingDateTime = DateTime.fromISO(requestedShippingDate);

    return requestedShippingDateTime > DateTime.utc();
};

/* eslint-disable-next-line max-statements */
const processNonAppointmentDelivery = (
    backorder: BackorderDelivery,
    futureDeliveries: (FutureDelivery & { amount: number })[]
) => {
    let unknownCount = 0;
    let stockAvailableNow = 0;
    let stockAvailableSoon = 0;
    let stockAvailableLater = 0;
    let stockAvailableDelay = 0;

    stockAvailableNow += backorder.stockAvailableNow;
    stockAvailableSoon += backorder.stockAvailableSoon;
    stockAvailableLater += backorder.stockAvailableLater;

    if (backorder.stockAvailableDelay) {
        let totalFutureDelivery = 0;
        for (const futureDelivery of backorder.futureDeliveries) {
            const existingEntry = futureDeliveries.find(
                item =>
                    item.calendarWeek === futureDelivery.calendarWeek &&
                    item.year === futureDelivery.year
            );

            if (existingEntry) {
                existingEntry.amount += futureDelivery.amount;
            } else {
                futureDeliveries.push({ ...futureDelivery });
            }

            totalFutureDelivery += futureDelivery.amount;
        }

        stockAvailableDelay +=
            backorder.stockAvailableDelay - totalFutureDelivery;
    }

    unknownCount +=
        backorder.pendingQuantity -
        backorder.stockAvailableNow -
        backorder.stockAvailableSoon -
        backorder.stockAvailableLater -
        backorder.stockAvailableDelay;

    return {
        unknownCount,
        stockAvailableNow,
        stockAvailableSoon,
        stockAvailableLater,
        stockAvailableDelay,
    };
};

/* eslint-disable-next-line max-statements */
const toBackorderBreakdown = (backorders: BackorderDelivery[]) => {
    const [{ backorderMarginDays }] = backorders;

    let unknownCount = 0;
    let stockAvailableNow = 0;
    let stockAvailableSoon = 0;
    let stockAvailableLater = 0;
    let stockAvailableDelay = 0;

    const futureDeliveries = [] as (FutureDelivery & { amount: number })[];

    const appointmentDeliveries: [string, number][] = [];

    for (const backorder of backorders) {
        if (
            backorder.requestedShippingDate &&
            isAppointmentDelivery(backorder.requestedShippingDate)
        ) {
            const existed = appointmentDeliveries.find(
                ([requestedShippingDate]) =>
                    requestedShippingDate === backorder.requestedShippingDate
            );
            if (existed) {
                existed[1] += backorder.pendingQuantity;
            } else {
                appointmentDeliveries.push([
                    backorder.requestedShippingDate,
                    backorder.pendingQuantity,
                ]);
            }
        } else {
            const counts = processNonAppointmentDelivery(
                backorder,
                futureDeliveries
            );

            unknownCount += counts.unknownCount;
            stockAvailableNow += counts.stockAvailableNow;
            stockAvailableSoon += counts.stockAvailableSoon;
            stockAvailableLater += counts.stockAvailableLater;
            stockAvailableDelay += counts.stockAvailableDelay;
        }
    }

    return {
        stockAvailableNow,
        stockAvailableSoon,
        stockAvailableLater,
        backorderMarginDays,
        appointmentDeliveries,
        futureDeliveries,
        stockAvailableDelay,
        unknownCount,
    };
};

const formatBackorders = (backorders: BackorderDelivery[]) => {
    return backorderBreakdownToMessages(toBackorderBreakdown(backorders));
};

export default formatBackorders;
